import React, { useState, useEffect } from 'react';
import { Link } from "react-router-dom";
import videofilewebm from '../../../Assests/Videos/showroom-video-webm.mkv';
import ScrollIndicator from '@/components/ui/scroll-indicator';

interface FloatingButtonProps {
    href: string;
    icon: string;
    label: string;
    index: number;
    delay: number;
    onClick?: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void; //2/9/25
}

const FloatingButton: React.FC<FloatingButtonProps> = ({ href, icon, label, index, delay, onClick }) => {
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => {
            setIsVisible(true);
        }, delay);

        return () => clearTimeout(timer);
    }, [delay]);

    return (
        <a
            href={href}
            onClick={onClick}
            className={`
                group relative flex items-center justify-center
                w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 lg:w-36 lg:h-36
                bg-white/15 backdrop-blur-sm border-3 sm:border-4 border-white/40
                rounded-full hero-floating-button
                transition-all duration-300 ease-out
                hover:bg-white/25 hover:scale-110 hover:border-white/70
                hover:shadow-2xl hover:shadow-white/30
                active:scale-105 active:bg-white/30
                cursor-pointer z-20
                ${isVisible
                    ? 'opacity-100 translate-x-0'
                    : 'opacity-0 translate-x-8'
                }
            `}
            data-delay={delay}
        >
            <img
                className="w-6 h-6 sm:w-7 sm:h-7 md:w-10 md:h-10 lg:w-14 lg:h-14 filter brightness-0 invert transition-transform duration-300 group-hover:scale-110"
                src={icon}
                alt={label}
            />

            {/* Tooltip */}
            <div className="
                absolute -top-14 left-1/2 -translate-x-1/2 px-4 py-2
                bg-black/90 backdrop-blur-sm text-white text-sm font-medium
                rounded-lg whitespace-nowrap
                opacity-0 translate-y-2 scale-95
                group-hover:opacity-100 group-hover:translate-y-0 group-hover:scale-100
                transition-all duration-300 ease-out
                pointer-events-none z-30
                hidden md:block
            ">
                {label}
                <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-t-4 border-t-black/90 border-x-4 border-x-transparent"></div>
            </div>
        </a>
    );
};

export default function HeroVideoWebM() {
    const [contentVisible, setContentVisible] = useState(false);
    const [interacted, setInteracted] = useState(false);
    const [videoError, setVideoError] = useState<string | null>(null);

    const handleFloatingButtonClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        e.preventDefault();
        const href = e.currentTarget.getAttribute('href') || '';
        const id = href.replace('#', '');
        const el = document.getElementById(id);
        if (el) {
            // Adjust offset for different screens if needed
            let offset = 16; // default offset for mobile
            if (window.innerWidth >= 1280) { // xl and up
                offset = 120; // adjust this value to match your left padding/sidebar
            }
            else if (window.innerWidth >= 1024) { // lg and up
                offset = 32;
            } else if (window.innerWidth >= 768) { // md and up
                offset = 24;
            }
            const y = el.getBoundingClientRect().top + window.pageYOffset - offset;
            window.scrollTo({ top: y, behavior: 'smooth' });
        }
    };

    useEffect(() => {
        // Show content after video has had time to load
        const timer = setTimeout(() => {
            setContentVisible(true);
        }, 1500);

        // Listen for user interactions
        const handleInteraction = () => {
            if (!interacted) {
                setInteracted(true);
                // Trigger navigation visibility
                window.dispatchEvent(new CustomEvent('heroInteraction'));
            }
        };

        window.addEventListener('scroll', handleInteraction);
        window.addEventListener('click', handleInteraction);
        window.addEventListener('touchstart', handleInteraction);

        return () => {
            clearTimeout(timer);
            window.removeEventListener('scroll', handleInteraction);
            window.removeEventListener('click', handleInteraction);
            window.removeEventListener('touchstart', handleInteraction);
        };
    }, [interacted]);

    useEffect(() => {
        const videoElement = document.querySelector('video');
        if (videoElement) {
            const handleError = (e: ErrorEvent) => {
                console.error('Video error:', e);
                setVideoError(e.message);
            };

            const handleLoadStart = () => {
                console.log('Video load started');
            };

            const handleLoadedData = () => {
                console.log('Video data loaded');
            };

            videoElement.addEventListener('error', handleError as EventListener);
            videoElement.addEventListener('loadstart', handleLoadStart);
            videoElement.addEventListener('loadeddata', handleLoadedData);

            return () => {
                videoElement.removeEventListener('error', handleError as EventListener);
                videoElement.removeEventListener('loadstart', handleLoadStart);
                videoElement.removeEventListener('loadeddata', handleLoadedData);
            };
        }
    }, []);

    const floatingButtons = [
        {
            href: "#appliances",
            icon: `${process.env.REACT_APP_BLOB_URL}/www/img/NewWebPhotos/Homepage/hnc_web_homepage_appliances_icon.svg`,
            label: "Appliances"
        },
        {
            href: "#furniture",
            icon: `${process.env.REACT_APP_BLOB_URL}/www/img/NewWebPhotos/Homepage/hnc_web_homepage_f&b_icon.svg`,
            label: "Furniture & Bedding"
        },
        {
            href: "#turn-key",
            icon: `${process.env.REACT_APP_BLOB_URL}/www/img/NewWebPhotos/Homepage/hnc_web_homepage_turn-key_icon.svg`,
            label: "Turn-Key Solutions"
        },
        {
            href: "#secure-casting",
            icon: `${process.env.REACT_APP_BLOB_URL}/www/img/NewWebPhotos/Homepage/hnc_web_homepage_secure_casting_icon.svg`,
            label: "Secure Guest Casting"
        }
    ];

    return (
        <div className="relative w-full h-screen overflow-hidden">
            {/* Full-screen video background */}
            <video
                className="absolute inset-0 w-full h-full object-cover"
                autoPlay
                loop
                muted
                playsInline
                controlsList="nodownload"
                preload="auto"
                poster="https://hncnzwww.blob.core.windows.net/www/showrooms/harvey-norman-commercial-showroom-christchurch-1.jpg"
                crossOrigin="anonymous"
            >
                {window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' ? (
                    <source src={videofilewebm} type="video/webm" />
                ) : (
                    <source src="https://hncnzwww.blob.core.windows.net/www/videos/showroom-video-webm.mkv" type="video/webm" />
                )}
            </video>

            {videoError && (
                <div className="absolute top-4 left-4 bg-red-500/80 text-white px-4 py-2 rounded">
                    Video error: {videoError}
                </div>
            )}

            {/* Dark overlay for better contrast and obscuring - darker on left */}
            {/* <div className="absolute inset-0 bg-black/50"></div>
            <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/40 to-black/60"></div>
            <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/40"></div> */}
            <div className="absolute inset-0 bg-black/40"></div>
            <div className="absolute inset-0 bg-gradient-to-r from-black/30 via-transparent to-transparent"></div>

            {/* Main content */}
            <div className="relative z-10 h-full flex items-start sm:items-center  justify-center  lg:justify-start px-4 md:px-8 lg:px-12 pt-32 sm:pt-0 md:pb-12">
                <div className={`
                    max-w-2xl text-center  lg:text-left
                    transition-all duration-1000 ease-out
                    ${contentVisible
                        ? 'opacity-100 translate-y-0'
                        : 'opacity-0 translate-y-8'
                    }
                `}>
                    {/* Main headline */}
                    <h1 className={`
                        text-2xl md:text-4xl lg:text-5xl xl:text-6xl
                        font-extrabold uppercase text-white
                        leading-tight mb-6
                        transition-all duration-1200 ease-out
                        ${contentVisible
                            ? 'opacity-100 translate-y-0'
                            : 'opacity-0 translate-y-12'
                        }
                    `}
                        data-delay={contentVisible ? '300' : '0'}
                    >
                        <span className="block text-white [text-shadow:2px_2px_4px_rgba(0,0,0,0.5)]">
                            Your One-stop
                        </span>
                        
                        <span className="block text-harvey-red relative [text-shadow:2px_2px_4px_rgba(0,0,0,0.5)]">
                            Business Solution
                        </span>
                        <span className="block text-white [text-shadow:2px_2px_4px_rgba(0,0,0,0.5)]">
                            Partner
                        </span>
                    </h1>
                    <div className='py-8 mb-6'>
                        <h1 className=' text-white [text-shadow:2px_2px_4px_rgba(0,0,0,0.5)] text-base md:text-lg font-semibold'>
                            Trade Pricing | Trusted Brands | Dedicated Support
                        </h1>
                    </div>

                    {/* Primary CTA */}
                    <div className={`
                        transition-all duration-1000 ease-out
                        ${contentVisible
                            ? 'opacity-100 translate-y-0'
                            : 'opacity-0 translate-y-8'
                        }
                    `}
                        data-delay={contentVisible ? '600' : '0'}
                    >
                        {/* 20251211 added btn for google analytics */}
                        <Link
                            to="/contact-us/"
                            className="
                            btn
                                inline-flex items-center justify-center
                                px-6 py-3 md:px-10 md:py-5
                                bg-gradient-to-r from-red-600 to-red-700
                                hover:from-red-700 hover:to-red-800
                                text-white
                                text-base md:text-lg font-semibold uppercase
                                rounded-full
                                shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300
                                group
                            "
                        >
                            Start Your Project Today
                            <svg
                                className="ml-3 w-5 h-5 md:w-6 md:h-6 transition-transform group-hover:translate-x-1"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                            </svg>
                        </Link>
                    </div>

                    {/* Secondary CTA */}
                    <div className={`
                        mt-6
                        transition-all duration-1000 ease-out
                        ${contentVisible
                            ? 'opacity-100 translate-y-0'
                            : 'opacity-0 translate-y-8'
                        }
                    `}
                        data-delay={contentVisible ? '800' : '0'}
                    >
                        <a
                            href="tel:0800222699"
                            className="
                            btn
                                inline-flex items-center
                                text-white text-base md:text-lg
                                transition-all duration-300
                                hover:text-red-400
                                group
                            "
                        >
                            <svg
                                className="mr-3 w-6 h-6 transition-transform group-hover:scale-110"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                            </svg>
                            Call 0800 222 699
                        </a>
                    </div>
                </div>
            </div>

            {/* Floating circular buttons in star layout - positioned on the right side */}
            {/* Desktop/Tablet: Star layout on the right. */}
            <div className="absolute inset-0 pointer-events-none hidden lg:block  z-20">
                {/* Top button - positioned top-center of right area */}
                <div className="absolute top-1/4 right-1/4 -translate-x-1/2 pointer-events-auto hero-star-top z-30">
                    <FloatingButton
                        href={floatingButtons[0].href}
                        icon={floatingButtons[0].icon}
                        label={floatingButtons[0].label}
                        index={0}
                        delay={1500}
                        onClick={handleFloatingButtonClick}
                    />
                </div>

                {/* Right button - positioned far right, center vertically */}
                <div className="absolute top-1/2 right-8 -translate-y-1/2 pointer-events-auto hero-star-right z-30">
                    <FloatingButton
                        href={floatingButtons[1].href}
                        icon={floatingButtons[1].icon}
                        label={floatingButtons[1].label}
                        index={1}
                        delay={1800}
                        onClick={handleFloatingButtonClick}
                    />
                </div>

                {/* Bottom button - positioned bottom-center of right area */}
                <div className="absolute bottom-1/4 right-1/4 -translate-x-1/2 pointer-events-auto hero-star-bottom z-30">
                    <FloatingButton
                        href={floatingButtons[2].href}
                        icon={floatingButtons[2].icon}
                        label={floatingButtons[2].label}
                        index={2}
                        delay={2100}
                        onClick={handleFloatingButtonClick}
                    />
                </div>

                {/* Left button - positioned left of the star pattern */}
                <div className="absolute top-1/2 right-2/5 -translate-y-1/2 pointer-events-auto hero-star-left z-30">
                    <FloatingButton
                        href={floatingButtons[3].href}
                        icon={floatingButtons[3].icon}
                        label={floatingButtons[3].label}
                        index={3}
                        delay={2400}
                        onClick={handleFloatingButtonClick}
                    />
                </div>
            </div>

            {/* Mobile: Single row layout centered below main content. */}
            <div className="absolute top-2/3 left-0 right-0 flex justify-center lg:hidden  z-20 py-8">
                <div className="flex gap-3 pointer-events-auto z-30 px-2 justify-center items-center w-full max-w-sm">
                    {floatingButtons.map((button, index) => (
                        <div key={button.href} className="flex-shrink-0">
                            <FloatingButton
                                href={button.href}
                                icon={button.icon}
                                label={button.label}
                                index={index}
                                delay={1500 + (index * 300)}
                                onClick={handleFloatingButtonClick}

                            />
                        </div>
                    ))}
                </div>
            </div>

            {/* Scroll indicator */}
            <div className={`
                absolute bottom-8 left-1/2 -translate-x-1/2
                transition-all duration-1000 ease-out
                ${contentVisible
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-4'
                }
            `}
                data-delay={contentVisible ? '1500' : '0'}
            >
                <ScrollIndicator label='SCROLL TO EXPLORE' />
            </div>
        </div>
    );
}